import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import VideoLibraryOutlinedIcon from "@mui/icons-material/VideoLibraryOutlined";
import {
  Box,
  Button,
  Chip,
  Dialog,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import moment from "moment";
import React, { forwardRef, useImperativeHandle, useState } from "react";
import { useTranslation } from "react-i18next";
import AppLoader from "../../../components/AppLoader";
import DialogBody from "../../../components/Dialog/Body";
import DialogHeader from "../../../components/Dialog/Header";
import Label from "../../../components/Label";
import TextInput from "../../../components/TextInputs";
import globalStyle from "../../../globalStyle";
import { reviewCampaignSubmission } from "../../../services/campaignApiFn";
import { handleOpenSnackBar } from "../../../utils";

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(2),
  },
  "& .MuiPaper-root": {
    width: "720px",
    maxWidth: "720px",
    [theme.breakpoints.down("md")]: {
      margin: 12,
    },
  },
}));

export const submissionStatusColor = (status) => {
  if (status === "submitted") return "warning";
  if (status === "approved") return "info";
  if (status === "posted") return "success";
  if (status === "rejected") return "error";
  return "default";
};

const InfoRow = ({ label, children }) => (
  <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
    <Typography variant="body2" color="text.secondary" minWidth={120}>
      {label}
    </Typography>
    <Typography variant="body2" fontWeight={500} component="div">
      {children}
    </Typography>
  </Stack>
);

const SubmissionReviewDialog = forwardRef(({ onReviewed }, ref) => {
  const { t } = useTranslation(["campaign", "common"]);
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [participant, setParticipant] = useState(null);
  const [showRejectInput, setShowRejectInput] = useState(false);
  const [reason, setReason] = useState("");
  const [reasonError, setReasonError] = useState(false);

  useImperativeHandle(ref, () => ({
    openDialog(params) {
      setParticipant(params.data);
      setOpen(true);
    },
    closeDialog() {
      handleClose();
    },
  }));

  const handleClose = () => {
    setOpen(false);
    setParticipant(null);
    setShowRejectInput(false);
    setReason("");
    setReasonError(false);
  };

  const handleReview = async (status) => {
    if (status === "rejected" && reason.trim().length < 3) {
      return setReasonError(true);
    }
    try {
      setIsLoading(true);
      const payload = { status };
      if (status === "rejected") payload.reason = reason.trim();
      const response = await reviewCampaignSubmission(
        participant._id,
        payload
      );
      if (response?.data?.status === "success") {
        handleOpenSnackBar("success", response.data.message);
        handleClose();
        onReviewed?.();
      } else {
        handleOpenSnackBar("error", response?.data?.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error?.message);
    } finally {
      setIsLoading(false);
    }
  };

  const submission = participant?.submission;
  const creator = participant?.creator || {};

  return (
    <React.Fragment>
      <AppLoader isVisible={isLoading} />
      <BootstrapDialog onClose={handleClose} open={open}>
        <DialogHeader
          title={t("videoSubmission")}
          secondaryHeading={creator.username}
          icon={<VideoLibraryOutlinedIcon />}
          onClose={handleClose}
        />
        <DialogBody>
          {submission && (
            <Stack gap={2}>
              <Box
                component="video"
                src={submission.video?.url}
                controls
                playsInline
                sx={{
                  width: "100%",
                  maxHeight: 460,
                  bgcolor: "black",
                  borderRadius: 2,
                }}
              />

              <Stack gap={1}>
                <InfoRow label={t("status", { ns: "common" })}>
                  <Chip
                    size="small"
                    color={submissionStatusColor(submission.status)}
                    label={t(`submissionStatus.${submission.status}`)}
                  />
                </InfoRow>
                <InfoRow label={t("submittedAt")}>
                  {moment(submission.submittedAt).format("DD/MM/YYYY hh:mm a")}
                </InfoRow>
                <InfoRow label={t("duration")}>
                  {t("secondsCount", {
                    count: Math.round(submission.video?.duration || 0),
                  })}
                </InfoRow>
                {submission.caption && (
                  <InfoRow label={t("caption")}>
                    <span style={{ whiteSpace: "pre-line" }}>
                      {submission.caption}
                    </span>
                  </InfoRow>
                )}
                {submission.status === "rejected" &&
                  submission.rejectionReason && (
                    <InfoRow label={t("rejectionReason")}>
                      {submission.rejectionReason}
                    </InfoRow>
                  )}
              </Stack>

              {submission.status === "posted" && (
                <>
                  <Divider />
                  <Stack gap={1}>
                    <Typography fontWeight={600}>
                      {t("postedLinks")}
                    </Typography>
                    {submission.postedAt && (
                      <Typography variant="body2" color="text.secondary">
                        {t("postedOn")}:{" "}
                        {moment(submission.postedAt).format("DD/MM/YYYY")}
                      </Typography>
                    )}
                    <Stack direction="row" gap={1} flexWrap="wrap">
                      {(submission.posts || []).map((post) => (
                        <Chip
                          key={post.platform}
                          label={t(post.platform)}
                          icon={<OpenInNewIcon fontSize="small" />}
                          component="a"
                          href={post.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          clickable
                          variant="outlined"
                        />
                      ))}
                    </Stack>
                  </Stack>
                </>
              )}

              {submission.status === "submitted" && (
                <>
                  <Divider />
                  {showRejectInput && (
                    <Box>
                      <Label labelText={t("provideReason", { ns: "common" })} />
                      <TextInput
                        styleProps={globalStyle.textField}
                        placeholder={t("rejectVideoReasonPlaceholder")}
                        multiline
                        rows={3}
                        inputError={reasonError}
                        helperText={
                          reasonError && t("validation.rejectReasonRequired")
                        }
                        value={reason}
                        onChange={(e) => {
                          setReasonError(false);
                          setReason(e.target.value);
                        }}
                      />
                    </Box>
                  )}
                  <Stack direction="row" gap={1} justifyContent="flex-end">
                    {showRejectInput ? (
                      <>
                        <Button
                          variant="outlined"
                          onClick={() => {
                            setShowRejectInput(false);
                            setReason("");
                            setReasonError(false);
                          }}
                        >
                          {t("cancel", { ns: "common" })}
                        </Button>
                        <Button
                          variant="contained"
                          color="error"
                          onClick={() => handleReview("rejected")}
                        >
                          {t("rejectVideo")}
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button
                          variant="outlined"
                          color="error"
                          onClick={() => setShowRejectInput(true)}
                        >
                          {t("reject")}
                        </Button>
                        <Button
                          variant="contained"
                          color="success"
                          onClick={() => handleReview("approved")}
                        >
                          {t("verifyVideo")}
                        </Button>
                      </>
                    )}
                  </Stack>
                </>
              )}
            </Stack>
          )}
        </DialogBody>
      </BootstrapDialog>
    </React.Fragment>
  );
});

export default SubmissionReviewDialog;
